import { z } from "zod";
import { SALARY_ADVANCE } from "../accounting";
import { summarizeShift } from "../reports";
import type { InventoryItem, StockMovementKind, Supplier } from "../types";
import { audit, can, fail, lkr, newId, parse, raiseAlert, requirePermission, type Ctx } from "./context";
import { warnIfLow } from "./orders";

const Id = z.string().min(1).max(128);
const Money = z.number().int().min(0).max(1_000_000_000);
const Qty = z.number().int().min(1).max(1_000_000_000);

function movement(ctx: Ctx, item: InventoryItem, kind: StockMovementKind, deltaMilli: number, unitCostMinor: number, refType: string, refId: string, reason: string): number {
  const balance = item.stockMilli + deltaMilli;
  ctx.tx.set("stockMovements", {
    id: newId(), inventoryItemId: item.id, itemName: item.name, kind, deltaMilli, balanceAfterMilli: balance, unitCostMinor, refType, refId, reason,
    createdBy: ctx.actor.id, createdByName: ctx.actor.name, createdAt: ctx.iso, businessDate: ctx.businessDate, schemaVersion: 1
  });
  return balance;
}

export async function saveInventoryItem(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "inventory.manage");
  const input = parse(z.object({
    id: Id.optional(), name: z.string().trim().min(1).max(80), unit: z.string().trim().min(1).max(12), reorderMilli: z.number().int().min(0).max(1_000_000_000).default(0), parMilli: z.number().int().min(0).max(1_000_000_000).default(0),
    purchaseUnits: z.array(z.object({ name: z.string().trim().min(1).max(20), milli: z.number().int().min(2).max(1_000_000_000) })).max(5).default([]),
    supplierId: Id.nullable().default(null), active: z.boolean().default(true), openingStockMilli: z.number().int().min(0).max(1_000_000_000).default(0), openingCostMinor: Money.default(0)
  }), raw);
  const existing = input.id ? await ctx.tx.get("inventoryItems", input.id) ?? fail("not-found", "Stock item not found.") : undefined;
  const { openingStockMilli, openingCostMinor, ...fields } = input;
  const item: InventoryItem = { stockMilli: 0, avgCostMinor: openingCostMinor, createdAt: ctx.iso, ...existing, ...fields, id: existing?.id ?? newId(), updatedAt: ctx.iso, schemaVersion: 1 };
  if (!existing && openingStockMilli > 0) item.stockMilli = movement(ctx, item, "adjustment", openingStockMilli, openingCostMinor, "opening", item.id, "Opening stock");
  ctx.tx.set("inventoryItems", item);
  audit(ctx, existing ? "inventory.item_updated" : "inventory.item_created", "inventoryItem", item.id, { name: item.name, active: item.active });
  return { id: item.id };
}

/** Stock and suppliers are kept on the POS (inventory.manage); the accountant keeps suppliers, their bills and payments too. */
export const requireSupplierAccess = (ctx: Ctx) => { if (!can(ctx.actor, "inventory.manage") && !can(ctx.actor, "accounts.manage")) fail("permission-denied", "You do not have permission for this action."); };

export async function saveSupplier(ctx: Ctx, raw: unknown) {
  requireSupplierAccess(ctx);
  const input = parse(z.object({
    id: Id.optional(), name: z.string().trim().min(1).max(80), phone: z.string().max(40).default(""), notes: z.string().max(300).default(""), active: z.boolean().default(true),
    contactPerson: z.string().trim().max(80).default(""), email: z.string().trim().max(120).default(""), address: z.string().trim().max(200).default(""), whatsapp: z.string().trim().max(40).default("")
  }), raw);
  const existing = input.id ? await ctx.tx.get("suppliers", input.id) ?? fail("not-found", "Supplier not found.") : undefined;
  const supplier: Supplier = { ...existing, ...input, id: existing?.id ?? newId(), schemaVersion: 1 };
  ctx.tx.set("suppliers", supplier);
  audit(ctx, existing ? "supplier.updated" : "supplier.created", "supplier", supplier.id, { name: supplier.name });
  return { id: supplier.id };
}

export const PurchaseInputSchema = z.object({
  idempotencyKey: z.string().uuid(), supplierId: Id.nullable().default(null), invoiceNo: z.string().max(60).default(""), onCredit: z.boolean().default(false),
  lines: z.array(z.object({ inventoryItemId: Id, qtyMilli: Qty, unitCostMinor: Money })).min(1).max(100),
  /** Input VAT on the invoice, on top of the stock's cost (VAT-registered restaurant and supplier). */
  vatMinor: Money.default(0)
});
export async function recordPurchase(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "inventory.manage");
  return addPurchase(ctx, parse(PurchaseInputSchema, raw));
}
/** Stock in at the weighted-average cost; on credit, the total is added to what is owed to the supplier. */
export async function addPurchase(ctx: Ctx, input: z.infer<typeof PurchaseInputSchema>) {
  const supplier = input.supplierId ? await ctx.tx.get("suppliers", input.supplierId) ?? fail("not-found", "Supplier not found.") : undefined;
  if (input.onCredit && !supplier) fail("invalid-argument", "Choose the supplier for a purchase on credit.");
  const id = newId();
  const lines = [];
  for (const line of input.lines) {
    const item = await ctx.tx.get("inventoryItems", line.inventoryItemId) ?? fail("not-found", "Stock item not found.");
    // Weighted-average cost; stock at or below zero simply adopts the new purchase cost.
    const avgCostMinor = item.stockMilli > 0 ? Math.round((item.stockMilli * item.avgCostMinor + line.qtyMilli * line.unitCostMinor) / (item.stockMilli + line.qtyMilli)) : line.unitCostMinor;
    const stockMilli = movement(ctx, item, "purchase", line.qtyMilli, line.unitCostMinor, "purchase", id, input.invoiceNo || "Purchase");
    ctx.tx.set("inventoryItems", { ...item, stockMilli, avgCostMinor, updatedAt: ctx.iso });
    lines.push({ ...line, name: item.name, lineMinor: Math.round((line.qtyMilli * line.unitCostMinor) / 1000) });
  }
  const totalMinor = lines.reduce((sum, line) => sum + line.lineMinor, 0);
  // The stock is at cost before VAT; the supplier is owed the invoice with its VAT.
  ctx.tx.set("purchases", { id, supplierId: supplier?.id ?? null, supplierName: supplier?.name ?? "", invoiceNo: input.invoiceNo, onCredit: input.onCredit, lines, totalMinor, ...(input.vatMinor > 0 ? { vatMinor: input.vatMinor } : {}), createdBy: ctx.actor.id, createdByName: ctx.actor.name, createdAt: ctx.iso, businessDate: ctx.businessDate, schemaVersion: 1 });
  if (input.onCredit && supplier) ctx.tx.set("suppliers", { ...supplier, balanceMinor: (supplier.balanceMinor ?? 0) + totalMinor + input.vatMinor });
  audit(ctx, "inventory.purchase_recorded", "purchase", id, { totalMinor, vatMinor: input.vatMinor, lines: lines.length, supplier: supplier?.name ?? "", onCredit: input.onCredit });
  return { id, totalMinor };
}

/**
 * Pays a supplier. Normally not more than is owed; marked as an advance, it may go beyond (the balance goes below zero and the
 * next bills use it up). Drawer cash is tied to the open shift.
 */
export async function paySupplier(ctx: Ctx, raw: unknown) {
  requireSupplierAccess(ctx);
  const input = parse(z.object({
    idempotencyKey: z.string().uuid(), supplierId: Id, amountMinor: z.number().int().positive().max(1_000_000_000),
    method: z.enum(["cash", "card", "bank_transfer"]), fromDrawer: z.boolean().default(false), note: z.string().trim().max(200).default(""), advance: z.boolean().default(false)
  }), raw);
  const supplier = await ctx.tx.get("suppliers", input.supplierId) ?? fail("not-found", "Supplier not found.");
  const owed = supplier.balanceMinor ?? 0;
  if (!input.advance && input.amountMinor > owed) fail("invalid-argument", "The payment is more than what is owed to this supplier. Mark it as an advance to pay ahead.");
  let shiftId: string | null = null;
  if (input.fromDrawer) {
    if (input.method !== "cash") fail("invalid-argument", "Only cash can be paid from the drawer.");
    shiftId = (await ctx.tx.query("shifts", [{ field: "status", op: "==", value: "open" }]))[0]?.id ?? fail("failed-precondition", "Open a cash shift before paying from the drawer.");
  }
  const id = newId(), balanceMinor = owed - input.amountMinor;
  ctx.tx.set("supplierPayments", {
    id, supplierId: supplier.id, supplierName: supplier.name, amountMinor: input.amountMinor, method: input.method, shiftId, note: input.note, balanceAfterMinor: balanceMinor, ...(input.advance ? { advance: true } : {}),
    createdBy: ctx.actor.id, createdByName: ctx.actor.name, createdAt: ctx.iso, businessDate: ctx.businessDate, schemaVersion: 1
  });
  ctx.tx.set("suppliers", { ...supplier, balanceMinor });
  audit(ctx, "supplier.paid", "supplier", supplier.id, { supplier: supplier.name, amountMinor: input.amountMinor, method: input.method, fromDrawer: input.fromDrawer, balanceMinor });
  return { id, balanceMinor };
}

export async function adjustStock(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "inventory.manage");
  const input = parse(z.object({
    idempotencyKey: z.string().uuid(), inventoryItemId: Id, kind: z.enum(["waste", "adjustment"]),
    deltaMilli: z.number().int().min(-1_000_000_000).max(1_000_000_000).refine((value) => value !== 0, "Enter a quantity"), reason: z.string().min(3).max(200)
  }), raw);
  const item = await ctx.tx.get("inventoryItems", input.inventoryItemId) ?? fail("not-found", "Stock item not found.");
  const delta = input.kind === "waste" ? -Math.abs(input.deltaMilli) : input.deltaMilli;
  const stockMilli = movement(ctx, item, input.kind, delta, item.avgCostMinor, input.kind, item.id, input.reason);
  ctx.tx.set("inventoryItems", { ...item, stockMilli, updatedAt: ctx.iso });
  warnIfLow(ctx, item, stockMilli);
  audit(ctx, input.kind === "waste" ? "inventory.waste_recorded" : "inventory.adjusted", "inventoryItem", item.id, { name: item.name, deltaMilli: delta, balanceMilli: stockMilli }, { reason: input.reason });
  return { stockMilli };
}

export async function createExpense(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "expenses.manage");
  const input = parse(z.object({
    idempotencyKey: z.string().uuid(), category: z.string().trim().min(2).max(40), description: z.string().trim().max(200).default(""),
    amountMinor: z.number().int().positive().max(1_000_000_000), method: z.enum(["cash", "card", "bank_transfer"]), fromDrawer: z.boolean().default(false),
    employeeId: z.string().min(1).max(64).optional()
  }), raw);
  // A salary advance names the person; payroll takes it off their next salary.
  let person: { employeeId: string; employeeName: string } | null = null;
  if (input.category === SALARY_ADVANCE) {
    const entry = input.employeeId ? await ctx.tx.get("staffRoster", input.employeeId) : null;
    if (!entry?.active) fail("invalid-argument", "Choose who the salary advance is given to.");
    person = { employeeId: entry.id, employeeName: entry.name };
  }
  let shiftId: string | null = null;
  if (input.fromDrawer) {
    if (input.method !== "cash") fail("invalid-argument", "Only cash expenses can be paid from the drawer.");
    shiftId = (await ctx.tx.query("shifts", [{ field: "status", op: "==", value: "open" }]))[0]?.id ?? fail("failed-precondition", "Open a cash shift before paying from the drawer.");
  }
  const id = newId();
  ctx.tx.set("expenses", { id, category: input.category, description: input.description, ...person, amountMinor: input.amountMinor, method: input.method, shiftId, voided: false, createdBy: ctx.actor.id, createdByName: ctx.actor.name, createdAt: ctx.iso, businessDate: ctx.businessDate, schemaVersion: 1 });
  audit(ctx, "expense.created", "expense", id, { category: input.category, ...person, amountMinor: input.amountMinor, method: input.method, fromDrawer: input.fromDrawer });
  return { id };
}

export async function voidExpense(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "expenses.manage");
  const input = parse(z.object({ expenseId: Id, reason: z.string().min(3).max(200) }), raw);
  const expense = await ctx.tx.get("expenses", input.expenseId) ?? fail("not-found", "Expense not found.");
  if (expense.voided) fail("failed-precondition", "This expense is already voided.");
  if (expense.shiftId && (await ctx.tx.get("shifts", expense.shiftId))?.status === "closed") fail("failed-precondition", "This expense belongs to a closed shift and can no longer be voided.");
  ctx.tx.set("expenses", { ...expense, voided: true, voidReason: input.reason });
  audit(ctx, "expense.voided", "expense", expense.id, { amountMinor: expense.amountMinor }, { reason: input.reason });
  return { id: expense.id };
}

export async function openShift(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "shifts.manage");
  const input = parse(z.object({ openingFloatMinor: Money }), raw);
  if ((await ctx.tx.query("shifts", [{ field: "status", op: "==", value: "open" }])).length > 0) fail("failed-precondition", "A shift is already open.");
  const id = newId();
  ctx.tx.set("shifts", { id, status: "open", openedBy: ctx.actor.id, openedByName: ctx.actor.name, openedAt: ctx.iso, openingFloatMinor: input.openingFloatMinor, note: "", businessDate: ctx.businessDate, schemaVersion: 1 });
  audit(ctx, "shift.opened", "shift", id, { openingFloatMinor: input.openingFloatMinor });
  return { id };
}

export async function closeShift(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "shifts.manage");
  const input = parse(z.object({ shiftId: Id, countedCashMinor: Money, note: z.string().max(300).default(""), closedByName: z.string().trim().min(2).max(60).optional() }), raw);
  const shift = await ctx.tx.get("shifts", input.shiftId) ?? fail("not-found", "Shift not found.");
  const closedByName = input.closedByName ?? ctx.actor.name;
  if (shift.status !== "open") fail("failed-precondition", "This shift is already closed.");
  const byShift = [{ field: "shiftId", op: "==" as const, value: shift.id }];
  const result = summarizeShift(shift, await ctx.tx.query("payments", byShift), await ctx.tx.query("refunds", byShift), await ctx.tx.query("expenses", byShift), await ctx.tx.query("supplierPayments", byShift));
  const varianceMinor = input.countedCashMinor - result.expectedCashMinor;
  ctx.tx.set("shifts", { ...shift, status: "closed", closedBy: ctx.actor.id, closedByName, ...(closedByName !== ctx.actor.name ? { closedOnAccount: ctx.actor.name } : {}), closedAt: ctx.iso, countedCashMinor: input.countedCashMinor, expectedCashMinor: result.expectedCashMinor, varianceMinor, summary: result.summary, note: input.note });
  audit(ctx, "shift.closed", "shift", shift.id, { expectedCashMinor: result.expectedCashMinor, countedCashMinor: input.countedCashMinor, varianceMinor, closedByName }, { reason: input.note });
  const { payments, refunds, cashExpensesMinor, orderCount } = result.summary;
  const refunded = refunds.cash + refunds.card + refunds.bank_transfer;
  raiseAlert(ctx, "day_end", `Shift closed: ${lkr(payments.cash + payments.card + payments.bank_transfer - refunded)} collected`,
    [`${orderCount} paid orders`, `Cash ${lkr(payments.cash)}`, `Card ${lkr(payments.card)}`, ...(refunded > 0 ? [`Refunds ${lkr(refunded)}`] : []), ...(cashExpensesMinor > 0 ? [`Paid out of the drawer ${lkr(cashExpensesMinor)}`] : []),
      `Drawer counted ${lkr(input.countedCashMinor)}`, varianceMinor === 0 ? "Drawer balanced" : `Drawer ${varianceMinor > 0 ? "over" : "SHORT"} by ${lkr(Math.abs(varianceMinor))}`, `Closed by ${closedByName}${closedByName !== ctx.actor.name ? ` (signed in as ${ctx.actor.name})` : ""}`].join(" · "));
  return { expectedCashMinor: result.expectedCashMinor, varianceMinor };
}

/**
 * Deletes a dish, menu category, stock item or supplier from the screens. The record is only marked as removed, so
 * old bills, reports and stock history keep their names; something still in use is refused with the reason.
 */
export async function removeRecord(ctx: Ctx, raw: unknown) {
  const input = parse(z.object({ kind: z.enum(["menuItem", "menuCategory", "inventoryItem", "supplier"]), id: Id }), raw);
  requirePermission(ctx, input.kind === "menuItem" || input.kind === "menuCategory" ? "menu.manage" : "inventory.manage");
  const dishes = (await ctx.tx.query("menuItems", [])).filter((dish) => !dish.removed);
  let name: string;
  if (input.kind === "menuItem") {
    const dish = await ctx.tx.get("menuItems", input.id) ?? fail("not-found", "Dish not found.");
    ctx.tx.set("menuItems", { ...dish, removed: true, active: false, updatedAt: ctx.iso });
    name = dish.name;
  } else if (input.kind === "menuCategory") {
    const category = await ctx.tx.get("menuCategories", input.id) ?? fail("not-found", "Category not found.");
    const inside = dishes.filter((dish) => dish.categoryId === category.id);
    if (inside.length > 0) fail("failed-precondition", `${category.name} still has ${inside.length} dish${inside.length > 1 ? "es" : ""} (${inside.slice(0, 3).map((dish) => dish.name).join(", ")}${inside.length > 3 ? "…" : ""}). Move or remove them first.`);
    ctx.tx.set("menuCategories", { ...category, removed: true, active: false });
    name = category.name;
  } else if (input.kind === "inventoryItem") {
    const item = await ctx.tx.get("inventoryItems", input.id) ?? fail("not-found", "Stock item not found.");
    const uses = (recipe: { inventoryItemId: string }[] | undefined) => (recipe ?? []).some((part) => part.inventoryItemId === item.id);
    const using = dishes.filter((dish) => uses(dish.recipe) || dish.sizes.some((size) => uses(size.recipe)) || dish.modifierGroups.some((group) => group.options.some((option) => uses(option.recipe))));
    if (using.length > 0) fail("failed-precondition", `${item.name} is in the recipe of ${using.slice(0, 3).map((dish) => dish.name).join(", ")}${using.length > 3 ? ` and ${using.length - 3} more` : ""}. Take it out of those recipes first.`);
    ctx.tx.set("inventoryItems", { ...item, removed: true, active: false, updatedAt: ctx.iso });
    name = item.name;
  } else {
    const supplier = await ctx.tx.get("suppliers", input.id) ?? fail("not-found", "Supplier not found.");
    if ((supplier.balanceMinor ?? 0) > 0) fail("failed-precondition", `${supplier.name} is still owed ${lkr(supplier.balanceMinor!)}. Pay it before removing the supplier.`);
    ctx.tx.set("suppliers", { ...supplier, removed: true, active: false });
    for (const item of await ctx.tx.query("inventoryItems", [{ field: "supplierId", op: "==", value: supplier.id }])) ctx.tx.set("inventoryItems", { ...item, supplierId: null, updatedAt: ctx.iso });
    name = supplier.name;
  }
  audit(ctx, "record.removed", input.kind, input.id, { name });
  return { ok: true };
}

/**
 * A stock count: the quantities found on the shelf replace what the system expected, one "count" movement per item
 * that differs. Returns the difference so the variance (missing or extra stock) can be shown at once.
 */
export async function countStock(ctx: Ctx, raw: unknown) {
  requirePermission(ctx, "inventory.manage");
  const input = parse(z.object({
    idempotencyKey: z.string().uuid(), note: z.string().trim().max(200).default(""),
    lines: z.array(z.object({ inventoryItemId: Id, countedMilli: z.number().int().min(0).max(1_000_000_000) })).min(1).max(300)
  }), raw);
  const countId = newId();
  const reason = input.note ? `Stock count: ${input.note}` : "Stock count";
  let missingMinor = 0, extraMinor = 0;
  const changed: { name: string; deltaMilli: number }[] = [];
  for (const line of input.lines) {
    const item = await ctx.tx.get("inventoryItems", line.inventoryItemId) ?? fail("not-found", "Stock item not found.");
    const delta = line.countedMilli - item.stockMilli;
    if (delta === 0) continue;
    const value = Math.round(Math.abs(delta) * item.avgCostMinor / 1000);
    if (delta < 0) missingMinor += value; else extraMinor += value;
    const stockMilli = movement(ctx, item, "count", delta, item.avgCostMinor, "count", countId, reason);
    ctx.tx.set("inventoryItems", { ...item, stockMilli, updatedAt: ctx.iso });
    warnIfLow(ctx, item, stockMilli);
    changed.push({ name: item.name, deltaMilli: delta });
  }
  audit(ctx, "inventory.counted", "stockCount", countId, { items: input.lines.length, changed: changed.length, missingMinor, extraMinor }, { reason });
  return { countId, counted: input.lines.length, changed: changed.length, missingMinor, extraMinor };
}
